import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { Download, FileText, BookOpen, Loader2 } from "lucide-react";
import {
  useEmagazinesByYear,
  Emagazine,
} from "@jambushrushti/hooks/useEmagazines";
import { formatCmsDate } from "@jambushrushti/lib/eventDate";

export default function EmagazinePage() {
  const { data: magazinesByYear, isLoading } = useEmagazinesByYear();
  const [selectedYear, setSelectedYear] = useState<string>("all");

  const years = magazinesByYear
    ? Object.keys(magazinesByYear).sort((a, b) => Number(b) - Number(a))
    : [];

  const filteredMagazines = () => {
    if (!magazinesByYear) {
      return [];
    }
    if (selectedYear === "all") {
      return Object.values(magazinesByYear).flat();
    }
    return magazinesByYear[Number(selectedYear)] || [];
  };

  const magazines = filteredMagazines();

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-primary/5 py-5 sm:py-8 lg:py-16">
          <div className="container mx-auto max-w-7xl px-4">
            <PageBreadcrumb
              items={[
                { label: "Home", href: "/" },
                { label: "eMagazine – Jambu Jyoti", href: "/emagazine" },
              ]}
            />
            <div className="mx-auto mt-4 max-w-3xl text-center sm:mt-8">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary sm:mb-4 sm:px-4 sm:py-2 sm:text-sm">
                <BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                Digital Publication
              </div>
              <h1 className="font-display mb-3 text-3xl font-bold text-foreground sm:mb-4 md:text-5xl">
                eMagazine – Jambu Jyoti
              </h1>
              <p className="text-base text-muted-foreground sm:text-lg">
                Explore our digital magazine collection featuring insights,
                articles, and teachings from the rich tradition of Jain
                scholarship and Gurudev's wisdom.
              </p>
            </div>
          </div>
        </section>

        {/* Archive Section */}
        <section className="py-6 sm:py-10 lg:py-16">
          <div className="container mx-auto max-w-7xl px-4">
            {/* Filters */}
            <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">
                  Magazine Archive
                </h2>
                <p className="text-muted-foreground">
                  Browse and download past issues
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Filter by year:
                </span>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Magazine Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            ) : magazines.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {magazines.map(magazine => (
                  <MagazineCard key={magazine.id} magazine={magazine} />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <FileText className="mx-auto mb-4 h-16 w-16 text-muted-foreground/50" />
                <h3 className="mb-2 text-xl font-semibold text-foreground">
                  No Issues Available
                </h3>
                <p className="text-muted-foreground">
                  {selectedYear === "all"
                    ? "Magazine issues will be available soon."
                    : `No issues found for ${selectedYear}.`}
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function MagazineCard({
  magazine,
  compact = false,
}: {
  magazine: Emagazine;
  compact?: boolean;
}) {
  return (
    <Card className="group overflow-hidden transition-all duration-300 hover:shadow-lg">
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5">
        {magazine.cover_image ? (
          <img
            src={magazine.cover_image}
            alt={magazine.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-primary/60">
            <FileText className="mb-2 h-16 w-16" />
            <span className="text-sm font-medium">Jambu Jyoti</span>
          </div>
        )}
        <div className="absolute right-3 top-3 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
          {magazine.year}
        </div>
      </div>
      <CardContent className={compact ? "p-3" : "p-4"}>
        <h3
          className={`line-clamp-2 font-semibold text-foreground ${compact ? "text-sm" : "text-base"} mb-1`}
        >
          {magazine.title}
        </h3>
        {magazine.volume && (
          <p className="mb-1 text-xs text-muted-foreground">
            {magazine.volume}
          </p>
        )}
        {magazine.issue_date && (
          <p className="mb-3 text-xs text-muted-foreground">
            {formatCmsDate(magazine.issue_date, "MMMM yyyy")}
          </p>
        )}
        {!compact && magazine.description && (
          <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
            {magazine.description}
          </p>
        )}
        <Button
          asChild
          size={compact ? "sm" : "default"}
          className="w-full gap-2"
        >
          <a href={magazine.pdf_url} target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4" />
            Download PDF
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}
